import React, { useMemo, useEffect, useState } from 'react'
import { OffertaType } from '@next-nx/utils/types'
import { useTrans, useTealium, useIsMobile } from '@next-nx/hooks'
import { numberNotation, priceNotation } from '@next-nx/utils/safe'
import { Button, OffertaCardTag, Tooltip, WImage } from '@next-nx/shared-ui/atoms'
import styles from './OffertaCard.module.sass'
import { ArrowRight, Shuffle } from '@next-nx/shared-ui/icons'
import Link from 'next/link'
import { getOffertaUrlParams, getPrezziContext, getTimestamp, getAnchorRel } from '@next-nx/utils'
import { useRouter } from 'next/router'

interface Props {
  offerta: OffertaType
  codiceCategoriaPrimaria?: string | undefined
  codiceCategoriaLanding?: string | undefined
  variant?: 'vertical' | 'horizontal' | 'menu'
  showIe?: boolean
  showIi?: boolean
  comparable?: boolean
  compareActive?: boolean
  onCompare?: () => void
  showTooltip?: boolean
  onClick?: () => void
  onCtaClick?: () => void
  className?: string
  cta?: string
  sezione?: string
  azione?: string
}

const OffertaCard = (props: Props) => {
  const {
    offerta,
    codiceCategoriaPrimaria = undefined,
    codiceCategoriaLanding = undefined,
    variant = 'vertical',
    comparable = false,
    compareActive = false,
    onCompare = () => undefined,
    showTooltip = false,
    onClick = undefined,
    onCtaClick = undefined,
    showIi: forceShowIi = false,
    showIe: forceShowIe = false,
    className = '',
    cta = '',
    sezione = '',
    azione = '',
  } = props

  const t = useTrans()
  const router = useRouter()
  const isMobile = useIsMobile()
  const { linkDataLayer, getProdottoData } = useTealium()
  const { categoriaCodice, showIi, showIe } = getPrezziContext(
    codiceCategoriaPrimaria,
    router?.query,
    forceShowIi,
    forceShowIe
  )
  const [comparaLabel, setcomparaLabel] = useState(t('Aggiunta auto al comparatore'))
  const [showTooltipLabel, setshowTooltipLabel] = useState(false)

  useEffect(() => {
    if (compareActive) {
      setcomparaLabel(t('Aggiunta auto al comparatore'))
      setTimeout(() => {
        setshowTooltipLabel(false)
      }, 2000)
    } else {
      setcomparaLabel(t('Rimossa auto dal comparatore'))
      setTimeout(() => {
        setshowTooltipLabel(false)
      }, 2000)
    }
  }, [showTooltipLabel])

  const imageBox = useMemo(
    () => (
      <WImage
        src={offerta.veicolo?.mainImmagineStatica || ''}
        alt={offerta.nome}
        title={offerta.nome}
        maxWidth={variant === 'horizontal' ? 207 : variant === 'menu' ? 176 : 227}
        maxHeight={variant === 'horizontal' ? 143 : variant === 'menu' ? 122 : 163}
        layout="fill"
        objectFit="contain"
        objectPosition="50% 50%"
      />
    ),
    [offerta?.nome]
  )

  const handleTracking = (product: string = '', interazione?: string, click_item?: OffertaType) => {
    const action_timestamp = getTimestamp()
    let dlObj = {}

    switch (interazione) {
      case 'click_item':
        dlObj = {
          virtual: 'action',
          action_name: 'item_click',
          action_tech: 'click',
          action_type: !!sezione.length ? sezione : 'carosello',
          action_timestamp,
          action_effect: 'click_prodotto',
          ...(click_item && { click_item: [getProdottoData(click_item, codiceCategoriaPrimaria)] }),
        }
        break
      case 'menu':
        dlObj = {
          virtual: 'action',
          action_name: 'menu',
          action_tech: 'click',
          action_type: sezione
            ?.replace(/\//g, '_')
            .replace(/\./g, '')
            .replace(/ /g, '_')
            .toLowerCase(),
          action_timestamp,
          action_effect: 'navigazione_menu',
          cta: 'in_evidenza',
          info: product.replace(/ /g, '_').replace(/\./g, '').toLowerCase(),
          ...(click_item && { click_item: [getProdottoData(click_item, codiceCategoriaPrimaria)] }),
        }
        break
      case 'ricerca':
        dlObj = {
          virtual: 'action',
          action_name: 'ricerca',
          action_tech: 'click',
          action_type: 'prodotti_in_evidenza',
          action_timestamp,
          action_effect: 'visualizzazione_risultati_ricerca',
          research_type: product.replace(/ /g, '_').replace(/\./g, '').toLowerCase(),
          ...(click_item && { click_item: [getProdottoData(click_item, codiceCategoriaPrimaria)] }),
        }
        break
      default:
        dlObj = {
          virtual: 'action',
          action_name: 'comparatore',
          action_tech: 'click',
          action_type: 'aggiungi_prodotto',
          action_timestamp,
          action_effect: 'aggiunta_prodotto_comparatore',
          cta: cta,
          product: product.replace(/ /g, '_').replace(/\./g, '').toLowerCase(),
        }
        break
    }
    linkDataLayer(dlObj)
  }

  return offerta ? (
    <div
      className={`${styles.root} ${offerta.autoMese ? styles['root--autoMese'] : ''} ${
        styles[`root--${variant}`]
      } ${className}`}
    >
      {(offerta.prontaConsegna || offerta.gammaEcologica) && (
        <div className={styles.tags}>
          {offerta.prontaConsegna && <OffertaCardTag label={t('Pronta consegna')} color="blue" />}
          {offerta.gammaEcologica && <OffertaCardTag label={t('Gamma ecologica')} color="green" />}
          {offerta.tuttoIncluso && <OffertaCardTag label={t('Tutto incluso')} color="purple" />}
        </div>
      )}
      {!onClick && !!offerta.url ? (
        <Link
          href={
            offerta.url + getOffertaUrlParams(offerta.url, categoriaCodice, codiceCategoriaLanding)
          }
        >
          <a
            className={`${styles.imageBox}`}
            rel={getAnchorRel(
              offerta.url +
                getOffertaUrlParams(offerta.url, categoriaCodice, codiceCategoriaLanding)
            )}
            onClick={() =>
              handleTracking(
                offerta?.veicolo?.nome,
                !!azione.length ? azione : 'click_item',
                offerta
              )
            }
          >
            {imageBox}
          </a>
        </Link>
      ) : (
        <div
          className={`${styles.imageBox}`}
          role={onClick ? 'button' : undefined}
          onClick={() => {
            onClick && onClick()
            handleTracking(offerta?.veicolo?.nome, !!azione.length ? azione : 'click_item', offerta)
          }}
        >
          {imageBox}
        </div>
      )}
      <div className={styles.content}>
        {offerta?.autoMese && <div className={styles.autoDelMese}>{t('Auto del mese')}</div>}
        <div className={styles.innerContent}>
          <div className={styles.titles}>
            {!onClick && !!offerta.url ? (
              <Link
                href={
                  offerta.url +
                  getOffertaUrlParams(offerta.url, categoriaCodice, codiceCategoriaLanding)
                }
              >
                <a
                  className={styles.title}
                  rel={getAnchorRel(
                    offerta.url +
                      getOffertaUrlParams(offerta.url, categoriaCodice, codiceCategoriaLanding)
                  )}
                  onClick={() =>
                    handleTracking(
                      offerta?.veicolo?.nome,
                      !!azione.length ? azione : 'click_item',
                      offerta
                    )
                  }
                >
                  {sezione === 'listing' ? (
                    <h3 className={styles.title}>{offerta.veicolo.nome}</h3>
                  ) : (
                    offerta.veicolo.nome
                  )}
                </a>
              </Link>
            ) : sezione === 'listing' ? (
              <h3
                className={styles.title}
                role={onClick ? 'button' : undefined}
                onClick={onClick}
                onKeyDown={onClick}
                tabIndex={onClick ? 0 : undefined}
              >
                {offerta.veicolo.nome}
              </h3>
            ) : (
              <p
                className={styles.title}
                role={onClick ? 'button' : undefined}
                onClick={onClick}
                onKeyDown={onClick}
                tabIndex={onClick ? 0 : undefined}
              >
                {offerta.veicolo.nome}
              </p>
            )}
            {!!offerta.veicolo?.allestimento && (
              <p className={styles.subtitle}>{offerta.veicolo.allestimento}</p>
            )}
          </div>
          {(!!offerta.condizioniMesi || !!offerta.condizioniKm) && (
            <p className={styles.condizioni}>
              {!!offerta.condizioniMesi && (
                <span>
                  {offerta.condizioniMesi} {t('mesi')}
                </span>
              )}
              {!!offerta.condizioniKm && (
                <span>
                  {numberNotation(offerta.condizioniKm)} {t('km/anno')}
                </span>
              )}
            </p>
          )}

          {/* Anticipo */}
          {offerta.anticipoZero ? (
            <p className={`${styles.anticipo} ${styles['anticipo--senza']}`}>
              {t('Senza anticipo')}
            </p>
          ) : showIi && showIe ? (
            <p className={`${styles.anticipo}`}>
              {!!offerta.anticipoIi && !!offerta.anticipoIe ? (
                variant === 'vertical' ? (
                  <>
                    <>
                      {t('Anticipo:')} {priceNotation(offerta.anticipoIi)}{' '}
                      <span className={styles.ivaText}>{t('i.i.')}</span>
                    </>
                    <>
                      {' '}
                      - {priceNotation(offerta.anticipoIe)}{' '}
                      <span className={styles.ivaText}>{t('i.e.')}</span>
                    </>
                  </>
                ) : (
                  <>
                    <span className={styles.anticipoTitle}>{t('Anticipo:')}</span>
                    <span className={styles.anticipoIi}>
                      <span className={styles.anticipoValue}>
                        {priceNotation(offerta.anticipoIi)}
                      </span>{' '}
                      <span className={styles.ivaText}>{t('iva inclusa')}</span>
                    </span>
                    <span className={styles.anticipoIe}>
                      <span className={styles.anticipoValue}>
                        {priceNotation(offerta.anticipoIe)}
                      </span>{' '}
                      <span className={styles.ivaText}>{t('iva esclusa')}</span>
                    </span>
                  </>
                )
              ) : null}
            </p>
          ) : showIi ? (
            <p className={`${styles.anticipo}`}>
              {!!offerta.anticipoIi &&
                (variant === 'vertical' ? (
                  <>
                    {t('Anticipo:')} {priceNotation(offerta.anticipoIi)}{' '}
                    <span className={styles.ivaText}>{t('iva inclusa')}</span>
                  </>
                ) : (
                  <>
                    <span className={styles.anticipoTitle}>{t('Anticipo:')}</span>
                    <span className={styles.anticipoIi}>
                      <span className={styles.anticipoValue}>
                        {priceNotation(offerta.anticipoIi)}
                      </span>{' '}
                      <span className={styles.ivaText}>{t('iva inclusa')}</span>
                    </span>
                  </>
                ))}
            </p>
          ) : showIe ? (
            <p className={`${styles.anticipo}`}>
              {!!offerta.anticipoIe &&
                (variant === 'vertical' ? (
                  <>
                    {t('Anticipo:')} {priceNotation(offerta.anticipoIe)}{' '}
                    <span className={styles.ivaText}>{t('iva esclusa')}</span>
                  </>
                ) : (
                  <>
                    <span className={styles.anticipoTitle}>{t('Anticipo:')}</span>
                    <span className={styles.anticipoIe}>
                      <span className={styles.anticipoValue}>
                        {priceNotation(offerta.anticipoIe)}
                      </span>{' '}
                      <span className={styles.ivaText}>{t('iva esclusa')}</span>
                    </span>
                  </>
                ))}
            </p>
          ) : null}

          {/* Prezzi */}
          {!offerta.prezzoIi && !offerta.prezzoIe ? (
            <></>
          ) : showIi && showIe ? (
            <div className={`${styles.prices} ${styles['prices--double']}`}>
              {!!offerta.prezzoIi && (
                <div className={styles.price}>
                  <span className={styles.finalPrice}>{priceNotation(offerta.prezzoIi)}</span>
                  <span className={styles.priceLabel}>{t('iva inclusa')}</span>
                </div>
              )}
              {!!offerta.prezzoIe && (
                <div className={`${styles.price} ${styles['price--ie']}`}>
                  <span className={styles.finalPrice}>{priceNotation(offerta.prezzoIe)}</span>
                  <span className={styles.priceLabel}>{t('iva esclusa')}</span>
                </div>
              )}
            </div>
          ) : showIi ? (
            <div className={`${styles.prices}`}>
              {!!offerta.prezzoIi && (
                <div className={styles.price}>
                  <span className={styles.finalPrice}>{priceNotation(offerta.prezzoIi)}</span>
                  <span className={styles.priceLabel}>{t('iva inclusa')}</span>
                </div>
              )}
            </div>
          ) : showIe ? (
            <div className={`${styles.prices}`}>
              {!!offerta.prezzoIe && (
                <div className={`${styles.price} ${styles['price--ie']}`}>
                  <span className={styles.finalPrice}>{priceNotation(offerta.prezzoIe)}</span>
                  <span className={styles.priceLabel}>{t('iva esclusa')}</span>
                </div>
              )}
            </div>
          ) : null}
        </div>
        {(!!offerta.url || !!onCtaClick || !!onCompare) && variant !== 'menu' && (
          <>
            <div
              className={`${styles.ctaBox} ${compareActive ? styles['ctaBox--compareActive'] : ''}`}
            >
              {!!onCompare &&
                (comparable || compareActive) &&
                (showTooltipLabel ? (
                  <div>
                    {/* Questi div vuoti servono per far funzionare il tooltip, non toglierli */}
                    <Tooltip
                      id={`offertacardtooltip__${offerta.pk}`}
                      className={styles.compareTooltip}
                      label={comparaLabel}
                      forceShow={matchMedia('(hover:none)').matches ? showTooltipLabel : undefined}
                    >
                      <div>
                        <Button
                          onClick={() => {
                            onCompare()
                            setshowTooltipLabel(true)
                            handleTracking(offerta?.nome)
                          }}
                          icon={<Shuffle />}
                          iconPos="right"
                          className={`${styles.compareCta} ${
                            compareActive ? styles['compareCta--active'] : ''
                          } ${'funzionalità_confronta'.toHotjarFormat('{0}', isMobile)}`}
                        />
                      </div>
                    </Tooltip>
                  </div>
                ) : (
                  <Button
                    onClick={() => {
                      onCompare()
                      setshowTooltipLabel(true)
                      handleTracking(offerta?.nome)
                    }}
                    icon={<Shuffle />}
                    iconPos="right"
                    className={`${styles.compareCta} ${
                      compareActive ? styles['compareCta--active'] : ''
                    } ${'funzionalità_confronta'.toHotjarFormat('{0}', isMobile)}`}
                  />
                ))}
              {(!!offerta.url || !!onCtaClick) && (
                <div className={styles.linkCtaBox}>
                  <Button
                    onClick={() => {
                      onCtaClick
                      handleTracking(
                        offerta?.veicolo?.nome,
                        !!azione.length ? azione : 'click_item',
                        offerta
                      )
                    }}
                    href={
                      onCtaClick
                        ? undefined
                        : offerta.url +
                          getOffertaUrlParams(
                            offerta.url || '',
                            categoriaCodice,
                            codiceCategoriaLanding
                          )
                    }
                    icon={<ArrowRight />}
                    iconPos="right"
                    className={styles.linkCta}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  ) : (
    <></>
  )
}

export default OffertaCard
